/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { zodResolver } from '@hookform/resolvers/zod';
import { Bug, Loader2, Upload, X, StopCircle } from 'lucide-react';
import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import * as z from 'zod';

import { useUserContext } from '../../pages/Root';
import { Label } from '../ui/label';

import { OUTPUT } from '@/commons/data';
import { MonitoredOrg, RunResultReport, SignedUrl } from '@/commons/protocols';
import { StatusIcon } from '@/components/atoms/StatusIcon';
import RoundToggle from '@/components/atoms/toggle/Toggle';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { useToast } from '@/components/ui/use-toast';
import { getMonitoredOrg, useCheckOrg, useGet, useMutate, useUser } from '@/net/commons';
import { CollapsibleSection } from '@/pages/projects/CollapsibleSection';

const reportFormSchema = z.object({
    feedback: z.string().trim().min(1, { message: 'Please provide feedback' }),
});

const convertAndCompressToJpeg = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = new OffscreenCanvas(img.width, img.height);
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(img.src);
                reject(new Error('Could not get canvas context'));
                return;
            }
            ctx.drawImage(img, 0, 0);
            canvas
                .convertToBlob({ type: 'image/jpeg', quality: 0.8 })
                .then((blob) => {
                    const newFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                        type: 'image/jpeg',
                    });
                    URL.revokeObjectURL(img.src);
                    resolve(newFile);
                })
                .catch((error) => {
                    URL.revokeObjectURL(img.src);
                    reject(error);
                });
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            reject(new Error('Failed to load image'));
        };
        img.src = URL.createObjectURL(file);
    });
};

const ReportDialog = ({
    currentOutput,
    currentReport,
    selectedRuns,
    handleSuccess,
    projectSlug = '',
    suiteSlug = '',
    executionId = '',
    runId = '',
}: {
    currentOutput?: OUTPUT;
    currentReport?: RunResultReport;
    handleSuccess?: () => void;
    selectedRuns?: { runId: number; executionId: number; storyId?: number }[];
    projectSlug?: string;
    suiteSlug?: string;
    executionId?: string;
    runId?: string;
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const { isOperator } = useUser();
    const { user } = useUserContext();
    const { canOperate } = user;
    const { organisation } = useCheckOrg();
    const currentOrgId = getMonitoredOrg()?.toString() ?? organisation?.id?.toString() ?? '';
    const [output, setOutput] = useState<OUTPUT | undefined>();
    const [files, setFiles] = useState<File[]>([]);
    const [convertingFiles, setConvertingFiles] = useState<Set<string>>(new Set());
    const abortController = useRef<AbortController | null>(null);
    const [shouldNotify, setShouldNotify] = useState(currentReport ? false : true);
    const [maintenance, setMaintenance] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const form = useForm<z.infer<typeof reportFormSchema>>({
        defaultValues: {
            feedback: currentReport?.feedback || '',
        },
        resolver: zodResolver(reportFormSchema),
    });
    const { data: monitoredOrgsData } = useGet<{ monitoredOrgs: MonitoredOrg[] }>(
        isOperator && user?.id ? `/bff/organisation/members/${user.id}/operator-organisations` : null,
    );

    const isOrgSpiko =
        organisation?.name === 'Spiko' ||
        monitoredOrgsData?.monitoredOrgs.find((org) => org.id.toString() === currentOrgId)?.name === 'Spiko';

    const { mutate, cache } = useSWRConfig();
    const { trigger: createReport } = useMutate(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/reports`,
        {
            method: 'POST',
            onSuccess: (data: any) => {
                const keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}`;
                const cachedData = cache.get(keyToUpdate)?.data;
                if (cachedData) {
                    mutate(keyToUpdate, {
                        ...cachedData,
                        ...data,
                        result: {
                            ...cachedData.result,
                            ...data.result,
                        },
                    });
                }
                handleSuccess?.();
            },
        },
    );
    const { trigger: updateReport } = useMutate(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/report/${currentReport?.id}`,
        {
            method: 'PUT',
            onSuccess: (data: any) => {
                const keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}`;
                const cachedData = cache.get(keyToUpdate)?.data;
                handleSuccess?.();
                if (cachedData) {
                    mutate(keyToUpdate, {
                        ...cachedData,
                        ...data,
                        result: {
                            ...cachedData.result,
                            ...data.result,
                        },
                    });
                }
            },
        },
    );
    const { trigger: batchCreateReport } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/batch-reports`, {
        method: 'POST',
        onSuccess: (data: any) => {
            handleSuccess?.();
            // Refresh all affected runs
            selectedRuns?.forEach((run) => {
                const keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${run.executionId}/run/${run.runId}`;
                mutate(keyToUpdate);
            });
        },
    });
    const { toast } = useToast();
    const missingValues = !form.getValues('feedback') || !output;

    const { trigger: triggerUploads } = useMutate<{ url: string; fileName: string }[]>(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/screenshots`,
        {
            method: 'POST',
        },
    );

    const { trigger: deleteScreenshot } = useMutate(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/report/${currentReport?.id}/screenshot/:id`,
        {
            method: 'DELETE',
            onSuccess: (data: any) => {
                const keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}`;
                const cachedData = cache.get(keyToUpdate)?.data;
                if (cachedData) {
                    const updatedData = {
                        ...cachedData,
                        result: {
                            ...cachedData.result,
                            report: {
                                ...cachedData.result.report,
                                screenshots: cachedData.result.report.screenshots.filter(
                                    (screenshot: SignedUrl) => screenshot.fileName !== data.fileName,
                                ),
                            },
                        },
                    };
                    mutate(keyToUpdate, updatedData);
                }
            },
        },
    );

    const onSubmit = async (values: z.infer<typeof reportFormSchema>) => {
        try {
            setIsSubmitting(true);

            let data: SignedUrl[] = [];
            if (files.length > 0) {
                data = await triggerUploads({
                    files: files.map((file) => ({
                        originalname: file.name,
                        contentType: file.type,
                    })),
                    runs: selectedRuns ? selectedRuns.map((run) => run.runId.toString()) : [runId],
                });

                await Promise.all(
                    data.map((d, i) =>
                        fetch(d.url, {
                            method: process.env.NODE_ENV === 'development' ? 'POST' : 'PUT',
                            headers: {
                                'Content-Type':
                                    process.env.NODE_ENV !== 'development' ? 'application/octet-stream' : '',
                            },
                            body: new Blob([files[i]]),
                        }),
                    ),
                );
            }

            const feedback = `${isOrgSpiko ? '<!subteam^S087P6VUUE8> ' : ''}${values.feedback}`;

            if (selectedRuns && selectedRuns.length > 1) {
                await batchCreateReport({
                    feedback,
                    shouldNotify,
                    output,
                    maintenance: maintenance ? maintenance : null,
                    screenshots: data.length > 0 ? data.map((f) => f.fileName) : null,
                    runs: selectedRuns.map((run) => ({
                        executionId: run.executionId,
                        runId: run.runId,
                    })),
                });
            } else if (currentReport) {
                await updateReport({
                    feedback,
                    shouldNotify,
                    output,
                    maintenance: maintenance ? maintenance : null,
                    screenshots: data.length > 0 ? data.map((f) => f.fileName) : null,
                });
            } else {
                await createReport({
                    feedback,
                    shouldNotify,
                    output,
                    maintenance: maintenance ? maintenance : null,
                    screenshots: data.length > 0 ? data.map((f) => f.fileName) : null,
                });
            }
            setFiles([]);
            setDialogOpen(false);
        } catch (err) {
            setIsSubmitting(false);
            toast({
                title: `Error submitting report`,
                description: "We couldn't submit your report. Please try again later.",
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (currentReport) {
            setShouldNotify(false);
        }
    }, [currentReport]);

    useEffect(() => {
        // when no current report and output becomes "DISMISSED" with no maintenance
        // set shouldNotify to false
        if (currentReport) {
            return;
        }
        if (output === 'DISMISSED' && !maintenance) {
            setShouldNotify(false);
        } else {
            setShouldNotify(true);
        }
    }, [output, maintenance]);

    const processFile = async (file: File): Promise<File | null> => {
        try {
            if (file.type.startsWith('image/')) {
                return await convertAndCompressToJpeg(file);
            }
            return file;
        } catch (error) {
            toast({
                title: 'Image conversion failed',
                description: `Could not convert "${file.name}". The file may be corrupted or in an unsupported format.`,
                variant: 'destructive',
            });
            return null;
        } finally {
            setConvertingFiles((prev) => {
                const next = new Set(prev);
                next.delete(file.name);
                return next;
            });
        }
    };

    const stopConversion = () => {
        if (abortController.current) {
            abortController.current.abort();
            abortController.current = null;
            setConvertingFiles(new Set());
            toast({
                title: 'Conversion cancelled',
                description: 'Image conversion has been cancelled.',
            });
        }
    };

    const processFiles = async (inputFiles: File[]): Promise<File[]> => {
        // Create new abort controller for this batch
        abortController.current = new AbortController();

        try {
            setConvertingFiles((prev) => {
                const next = new Set(prev);
                inputFiles.forEach((file) => next.add(file.name));
                return next;
            });

            const results = await Promise.all(
                inputFiles.map(async (file) => {
                    try {
                        if (abortController.current?.signal.aborted) {
                            return null;
                        }
                        return await processFile(file);
                    } catch (error) {
                        toast({
                            title: 'Image conversion failed',
                            description: `Could not convert "${file.name}". The file may be corrupted or in an unsupported format.`,
                            variant: 'destructive',
                        });
                        return null;
                    } finally {
                        setConvertingFiles((prev) => {
                            const next = new Set(prev);
                            next.delete(file.name);
                            return next;
                        });
                    }
                }),
            );

            return results.filter((file): file is File => file !== null);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                return [];
            }
            throw error;
        } finally {
            abortController.current = null;
        }
    };

    const handleDrop = async (e: DragEvent) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        const convertedFiles = await processFiles(droppedFiles);
        setFiles((prevFiles) => [...prevFiles, ...convertedFiles]);
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const convertedFiles = await processFiles(newFiles);
            setFiles((prevFiles) => [...prevFiles, ...convertedFiles]);
        }
    };

    const removeFile = (index: number) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const reportForm = (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
                <div className="text-sm text-muted-foreground grid grid-cols-3 h-full gap-3 items-center">
                    <Toggle
                        pressed={output === 'DISMISSED' && maintenance}
                        onPressedChange={() => {
                            setOutput('DISMISSED');
                            setMaintenance(true);
                        }}
                        variant="outline"
                        aria-label="Needs maintenance"
                        className="flex flex-row gap-2 justify-start"
                    >
                        <StatusIcon output="DISMISSED" maintenance size={20} />
                        <span className="whitespace-nowrap text-black">Maintenance</span>
                    </Toggle>
                    <span className="col-span-2">The story needs maintenance</span>
                    <Toggle
                        pressed={output === 'DISMISSED' && !maintenance}
                        onPressedChange={() => {
                            setOutput('DISMISSED');
                            setMaintenance(false);
                        }}
                        variant="outline"
                        aria-label="Mark as flake"
                        className="flex flex-row gap-2 justify-start"
                    >
                        <StatusIcon output="DISMISSED" size={20} />
                        <span className="whitespace-nowrap text-black">Mark as Flake</span>
                    </Toggle>
                    <span className="col-span-2">There's a bug in the agent</span>
                    <Toggle
                        pressed={output === 'FAIL'}
                        onPressedChange={() => {
                            setOutput('FAIL');
                            setMaintenance(false);
                        }}
                        variant="outline"
                        aria-label="Mark as fail"
                        className="flex flex-row gap-2 justify-start"
                    >
                        <StatusIcon output="FAIL" size={20} />
                        <span className="whitespace-nowrap text-black">Mark as Fail</span>
                    </Toggle>
                    <span className="col-span-2">The agent caught a real failure</span>
                    <div className="col-span-3 flex items-center justify-between">
                        <label htmlFor="shouldNotify" className="col-span-3">
                            Should we notify about this report?
                        </label>
                        <RoundToggle
                            defaultValue={shouldNotify}
                            id="shouldNotify"
                            onClick={() => setShouldNotify((prev) => !prev)}
                        />
                    </div>
                    <div
                        className="border-2 border-dashed rounded-lg p-6 text-center my-3 col-span-3"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        <input
                            type="file"
                            className="hidden"
                            id="file-upload"
                            onChange={handleFileChange}
                            accept="image/*"
                            multiple
                        />
                        <Label
                            htmlFor="file-upload"
                            className="cursor-pointer text-sm text-muted-foreground hover:text-foreground"
                        >
                            <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                            <span className="mt-2 block">Drag and drop screenshots here, or click to select files</span>
                        </Label>
                        {(files.length > 0 || convertingFiles.size > 0) && (
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                {files.map((file, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={`Screenshot ${index + 1}`}
                                            className="w-full object-cover rounded-md aspect-[16/9]"
                                        />
                                        <button
                                            onClick={() => removeFile(index)}
                                            className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="h-4 w-4 text-white" />
                                        </button>
                                    </div>
                                ))}
                                {convertingFiles.size > 0 && (
                                    <div className="relative group">
                                        <div className="w-full rounded-md aspect-[16/9] bg-slate-100 flex items-center justify-center">
                                            <button
                                                onClick={stopConversion}
                                                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Stop conversion"
                                            >
                                                <StopCircle className="h-4 w-4 text-white" />
                                            </button>
                                            <div className="flex flex-col items-center gap-2 text-slate-500">
                                                <div className="relative">
                                                    <Loader2 className="h-8 w-8 animate-spin" />
                                                </div>
                                                <span className="text-sm">
                                                    Converting {convertingFiles.size} image
                                                    {convertingFiles.size > 1 ? 's' : ''}...
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <FormField
                        control={form.control}
                        name="feedback"
                        render={({ field }) => (
                            <FormItem className="col-span-3">
                                <FormControl>
                                    <Textarea
                                        placeholder="The submit button failed but the agent missed it..."
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                        }}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <DialogFooter className="mt-3 sm:justify-end">
                    <div className="flex flex-row sm:space-x-2">
                        <Button
                            type="submit"
                            disabled={missingValues || form.formState.isSubmitting || convertingFiles.size > 0}
                            variant="secondary"
                        >
                            {form.formState.isSubmitting || isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </Form>
    );

    return (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
                {selectedRuns && selectedRuns.length > 1 ? (
                    <Button variant="outline" size="default" className="gap-x-2">
                        <Bug size="16" className="shrink-0" /> Report ({selectedRuns.length})
                    </Button>
                ) : !currentReport ? (
                    <Button variant="outline" size="default" className="gap-x-2">
                        <Bug size="16" className="shrink-0" /> Report
                    </Button>
                ) : (
                    <Button variant="outline" size="default" className="gap-x-2">
                        <Bug size="16" className="shrink-0" /> Marked as {currentOutput}
                    </Button>
                )}
            </DialogTrigger>
            {currentReport && (
                <DialogContent className="max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Your report</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="[word-break:break-word]">
                        Feedback: {currentReport.feedback}
                    </DialogDescription>
                    {currentReport.screenshots && currentReport.screenshots.length > 0 && (
                        <div className="mt-4">
                            <h3 className="text-lg font-medium text-slate-800 mb-4">Screenshots</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {currentReport.screenshots.map((screenshot, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={screenshot.url}
                                            className="w-full h-auto rounded-lg border border-slate-200 transition-transform group-hover:scale-[1.02] aspect-[16/9] object-cover cursor-pointer"
                                            alt={`Screenshot ${index + 1}`}
                                        />
                                        <span className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded-md text-sm">
                                            #{index + 1}
                                        </span>
                                        {canOperate && (
                                            <button
                                                onClick={() => deleteScreenshot({ idParam: screenshot.fileName })}
                                                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="h-4 w-4 text-white" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <CollapsibleSection theme="small" title="Update report">
                        <div className="pt-2">{reportForm}</div>
                    </CollapsibleSection>
                </DialogContent>
            )}
            {!currentReport && (
                <DialogContent className="max-h-[85vh] overflow-y-auto w-[600px]">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedRuns && selectedRuns.length > 1
                                ? `Set output for ${selectedRuns.length} runs`
                                : 'What should be the expected output of this run?'}
                        </DialogTitle>
                    </DialogHeader>
                    {reportForm}
                </DialogContent>
            )}
        </Dialog>
    );
};

export default ReportDialog;
